<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-imageSettings-styleAndAnimation"
  >
    <template
      v-if="hideOpacityControls"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />
    </template>

    <!-- Data Mapping Controls -->
    <template
      v-if="!isSocialIcon && !editingMultiple"
    >
      <div
        v-if="dataProvider"
        class="margin-top-md"
      >
        <p
          v-if="checkMapped('imageUrl')"
          class="sidebar-form-label"
        >
          From Data Provider
        </p>
        <map-data-control
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          :mapped="checkMapped('imageUrl')"
          property="imageUrl"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
      </div>

      <label
        v-if="dataProvider"
        class="sidebar-form-label"
        data-test="graphics-imageSettings-fallbackImage"
      >
        {{ checkMapped('imageUrl') ? 'Fallback image' : 'Static image' }}
      </label>
      <asset-changer
        :preview-image="imageUrl"
        asset-type="image"
        @modal="$emit('modal', $event)"
        @select-asset="changeImage"
        @remove-asset="removeImage"
      />
    </template>

    <map-data-control
      v-if="isSocialIcon"
      :data-provider="dataProvider"
      :element="element"
      label="Network Icon Style"
      control-type="select"
      property="imageUrl"
      :mapped="checkMapped('imageUrl')"
      :no-unmap="true"
      @update-map="mapToField"
      @clear-map="clearMapping"
    />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Fill type</label>
      <select
        v-model="fillType"
        :disabled="mask"
        data-test="graphics-imageSettings-selectFillType"
        data-entity="graphics-imageSettings-selectFillType"
      >
        <option value="stretch">
          Stretch
        </option>
        <option value="fit">
          Fit
        </option>
        <option value="fill">
          Fill
        </option>
      </select>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Border
        <input
          v-model="border"
          type="checkbox"
          data-test="graphics-imageSettings-toggleBorder"
          data-entity="graphics-imageSettings-toggleBorder"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div
        v-if="border"
        class="settings-row settings-row-xs"
      >
        <div class="settings-col-6 settings-col-xs">
          <validation-field as="input"
            v-model.number="borderWidth"
            rules="required|numeric|min:0"
            type="number"
            name="borderWidth"
            min="0"
            :class="{ 'is-invalid': validationErrors['borderWidth'] }"
            data-test="graphics-imageSettings-setBorderWidth"
            data-entity="graphics-imageSettings-setBorderWidth"
          />
          <label class="sidebar-input-sub-label">Width</label>
        </div>
        <div class="settings-col-6 settings-col-xs colorpicker">
          <colorpicker v-model="borderColor" />
          <label class="sidebar-input-sub-label">Color</label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <box-shadow-settings :element="element"></box-shadow-settings>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Blur
        <input
          v-model="blur"
          type="checkbox"
          data-test="graphics-imageSettings-toggleBlur"
          data-entity="graphics-imageSettings-toggleBlur"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div
        v-if="blur"
        class="input-group"
      >
        <validation-field as="input"
          v-model.number="blurSize"
          rules="required|numeric|min:0"
          type="number"
          name="blurSize"
          min="0"
          :class="{ 'is-invalid': validationErrors['blurSize'] }"
          data-test="graphics-imageSettings-setBlurSize"
          data-entity="graphics-imageSettings-setBlurSize"
        />
        <span class="input-group-addon">px</span>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Mask
        <input
          v-model.lazy="mask"
          type="checkbox"
          data-test="graphics-imageSettings-toggleMask"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div class="sidebar-help-text">
        Image will fill the entire shape and ignore "Fill type" (set above).
      </div>

      <div
        v-if="mask"
        class="sidebar-sub-group"
      >
        <label class="sidebar-form-label">Shape</label>
        <select
          v-model="maskShape"
          data-test="graphics-imageSettings-selectMaskShape"
          data-entity="graphics-imageSettings-selectMaskShape"
        >
          <option value="oval">
            Oval
          </option>
          <option value="rectangle">
            Rectangle
          </option>
        </select>
      </div>

      <div
        v-if="mask && maskShape === 'rectangle'"
        class="sidebar-sub-group"
        data-test="graphics-imageSettings-maskBorderRadius"
      >
        <border-radius
          v-model:top-left="borderTopLeftRadius"
          v-model:top-right="borderTopRightRadius"
          v-model:bottom-left="borderBottomLeftRadius"
          v-model:bottom-right="borderBottomRightRadius"
        />
      </div>

      <div
        v-if="mask"
        class="sidebar-sub-group position"
        data-test="graphics-imageSettings-maskPosition"
      >
        <label class="sidebar-form-label">Position</label>

        <div class="mask-position-controls">
          <button
            v-for="p in maskPositions"
            :key="p"
            class="mask-position-controls__control"
            :class="{
              'center': p === 'center-center',
              'selected': maskPosition === p,
            }"
            data-test="graphics-imageSettings-setMaskPosition"
            data-entity="graphics-imageSettings-setMaskPosition"
            @click="maskPosition = p"
          >
            <span class="sr-only">{{ p }}</span>
          </button>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </SidebarSettingsGroup>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import AssetChanger from './AssetChanger.vue';
import MapDataControl from './Data/MapDataControl.vue';
import BorderRadius from './BorderRadius.vue';

export default {
  emits: ['modal'],
  components: {
    'animation-settings': AnimationSettings,
    'opacity-settings': OpacitySettings,
    'box-shadow-settings': BoxShadowSettings,
    AssetChanger,
    MapDataControl,
    BorderRadius,
  },
  mixins: [SettingsMixins],
  props: {
    element: {
      type: Object,
      required: true,
    },
    scrollOffset: { required: true },
  },
  data() {
    return {
      maskPositions: [
        'top-left',
        'top-center',
        'top-right',
        'center-left',
        'center-center',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ],
    };
  },
  computed: {
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    fillType: {
      get() {
        return this.getPropertyValue('fillType');
      },
      set(v) {
        this.updateOneElementValue('fillType', v);
      },
    },
    border: {
      get() {
        return this.getPropertyValue('border');
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },
    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },
    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { mappedPath: 'borderColor' });
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },
    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },
    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
    mask: {
      get() {
        return this.getPropertyValue('mask');
      },
      set(v) {
        this.updateOneElementValue('mask', v);
      },
    },
    maskShape: {
      get() {
        return this.getPropertyValue('maskShape');
      },
      set(v) {
        this.updateOneElementValue('maskShape', v);
      },
    },
    maskPosition: {
      get() {
        return this.getPropertyValue('maskPosition', { fallbackValue: 'center-center' });
      },
      set(v) {
        this.updateOneElementValue('maskPosition', v);
      },
    },
    borderTopLeftRadius: {
      get() {
        return this.getPropertyValue('borderTopLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },
    borderTopRightRadius: {
      get() {
        return this.getPropertyValue('borderTopRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },
    borderBottomRightRadius: {
      get() {
        return this.getPropertyValue('borderBottomRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },
    borderBottomLeftRadius: {
      get() {
        return this.getPropertyValue('borderBottomLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },
    hideOpacityControls() {
      return !this.isSocialIcon;
    },
    isSocialIcon() {
      return this.element.data?.type === 'social-network-attribution';
    },
    imageUrl() {
      return this.getPropertyValue('imageUrl', { emptyValue: '' });
    },
  },
  methods: {
    changeImage({ asset, meta: imageMeta = {} }) {
      this.updateOneElementValue('imageUrl', asset?.media_url || '');
      this.updateOneElementValue('imageMeta', imageMeta);
    },
    removeImage() {
      this.updateOneElementValue('imageUrl', '');
      this.updateOneElementValue('imageMeta', {});
    },
  },
};
</script>
